import * as React from 'react';
import {
  InputErrorMessageSeverityType,
  MultiSelectProps,
  SelectItem,
  SelectView,
  SelectViewBaseProps,
  SelectViewProps
} from './Select.view';
import useWhatInput from 'react-use-what-input';
import { useCallback, useEffect, useRef, useState } from 'react';
import { WhatInputType } from 'what-input';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { useDimensions } from '../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';

export type SelectControllerProps = Omit<
  SelectViewProps,
  | 'chooseLabel'
  | 'dropdownContainerHeightType'
  | 'expanded'
  | 'hasOptionLabels'
  | 'intentType'
  | 'optGroupTuples'
  | 'label'
  | 'setExpanded'
  | 'selectedLabel'
  | 'updateValue'
  | 'selectedValue'
> & {
  // extend your SelectViewProps
  label?: string;
  action?: FormElementAction;
  chooseLabel?: string;
  selectedValue?: FormElementValueType;
  overrideIntent?: WhatInputType;
  onExpand?: () => void;
  onClose?: () => void;
  expanded?: boolean;
  deferFormChangeOnSelectedValue?: boolean;
};

// export type SelectControllerProps = SelectControllerBaseProps & MultiSelectProps & InputErrorMessageSeverityType;

const getClosestLabel = (items: (SelectItem | string)[], currentValue: FormElementValueType): string | null => {
  const localItems = [...items];
  const selected = localItems.find(item => (item as SelectItem)?.value === currentValue) as SelectItem;
  // if items have no opt groups just return null
  if (!selected || !localItems.filter(item => typeof item === 'string').length) {
    return null;
  }
  const indexOfItem = localItems.findIndex(item => (item as SelectItem)?.value === selected.value);
  return localItems.reduce((acc, current) => {
    if (typeof current === 'string' && items.indexOf(current) < indexOfItem) {
      return current;
    }
    return acc;
  }) as string;
};

const getSelectedLabel = (
  items: (SelectItem | string)[],
  selectedValue: FormElementValueType,
  chooseLabel: string,
  multiple: boolean
): string | string[] => {
  if (!multiple) {
    return (
      (
        items.find(item => {
          if (typeof item !== 'string' && selectedValue && item.value === selectedValue) {
            return item.label;
          }
          return false;
        }) as SelectItem
      )?.label || chooseLabel
    );
  }
  const selectedArray = (selectedValue as readonly string[]) || [];
  const selectedValues = (
    items.filter(item => {
      if (typeof item !== 'string' && selectedArray.includes(item.value as string)) {
        return item.label;
      }
      return false;
    }) as SelectItem[]
  ).map(value => value.label);
  return selectedValues.length ? selectedValues : chooseLabel;
};

export const SelectController = (props: SelectControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { useFormInstance, action, name, overrideIntent, items, inlinedLabel, selectedValue, onExpand, onClose } =
    props;
  const useFormInstanceRef = useRef<UseFormReturn>(useFormInstance);
  const initialIntentType = useWhatInput('intent');
  const intentType = overrideIntent ? overrideIntent : initialIntentType;
  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const { multiple, errorMessage, errorSeverity, visibleMultipleValues, ...viewProps } = props;
  const chooseLabel = props.chooseLabel || 'Select';
  const bodyRef = useRef<HTMLElement>(document.body);
  const { height: viewportHeight } = useDimensions(bodyRef);
  const dropdownContainerHeightType =
    viewportHeight && viewportHeight <= BreakpointsValuesEnum.tabletportrait ? 'mobile' : 'desktop';

  const [expanded, setExpanded] = useState<boolean>(false);
  const [executing, setExecuting] = useState<boolean>(false);
  const watchedValue = useWatch({
    control: useFormInstance.control,
    name
  });
  const watchedValueRef = useRef(watchedValue);
  const [selectedLabel, setSelectedLabel] = useState<string | string[]>(
    getSelectedLabel(items, watchedValue, chooseLabel, !!multiple)
  );
  const label = (inlinedLabel && getClosestLabel(items, watchedValue)) || props.label;

  const hasOptionLabels = !!items.find(item => typeof item === 'string');

  let optGroupTuples: [string, SelectItem[]][] = [];
  let currentIndex = 0;
  let optionGroupLabel: string;

  items.forEach((item, index) => {
    // only checks for the optgroup items since they define breakpoints
    const arrayToManipulateWith = [...items];
    if (typeof item === 'string') {
      if (index === 0 && hasOptionLabels) {
        optionGroupLabel = item;
        currentIndex = 1;
      } else {
        const arrayChunk = arrayToManipulateWith.splice(currentIndex, index - currentIndex);
        optGroupTuples = [...optGroupTuples, [optionGroupLabel, arrayChunk as SelectItem[]]];
        currentIndex = index + 1;
        optionGroupLabel = item;
      }
    } else if (index === items.length - 1 && hasOptionLabels) {
      const arrayChunk = arrayToManipulateWith.splice(currentIndex);
      optGroupTuples = [...optGroupTuples, [optionGroupLabel, arrayChunk as SelectItem[]]];
    }
  });

  const setFormValue = useCallback(
    (value: FormElementValueType, oldValue: FormElementValueType) => {
      useFormInstanceRef.current.setValue(name, value, { shouldValidate: true, shouldTouch: true });

      if (action) {
        setExecuting(true);
        action(value || '', (keepNewVal = true) => {
          if (!keepNewVal) {
            useFormInstanceRef.current.setValue(name, oldValue, { shouldValidate: true, shouldTouch: true });
          }
          setExecuting(false);
        });
      }
    },
    [setExecuting, action, name]
  );

  const updateValue = useCallback(
    (value: FormElementValueType) => {
      const originalValue: FormElementValueType = watchedValue;
      if (!multiple) {
        if (originalValue !== value) {
          setFormValue(value, originalValue);
        }
        setExpanded(false);
      } else {
        const originalArray = originalValue ? [...(originalValue as readonly string[])] : [];
        let newValue: string[] = []; // we set it to [] since if we pass undefined as a value, we want to remove all items from selected and set value to [];
        if (value === undefined) {
          setFormValue(newValue, originalValue);
          return;
        }
        const isInValues = originalArray.findIndex(item => item === value);

        if (isInValues > -1) {
          newValue = [...originalArray];
          newValue.splice(isInValues, 1);
        } else {
          newValue = [...originalArray, value as string];
        }

        newValue.sort();
        setFormValue(newValue, originalValue);
      }
    },
    [watchedValue, multiple, setFormValue, setExpanded]
  );

  // watch if default value has been changed and update
  useEffect(() => {
    // If the component implementing this Select has fine grain control of the form state.
    if (props.deferFormChangeOnSelectedValue) {
      return;
    }
    if (typeof watchedValueRef.current === 'object' || (typeof watchedValueRef.current === 'undefined' && multiple)) {
      const transformedValue = watchedValueRef.current ? (watchedValueRef.current as readonly string[]) : [];
      const sortedWatch = [...transformedValue].sort();
      const transformedSelected = selectedValue ? (selectedValue as readonly string[]) : [];
      const sortedSelected = [...transformedSelected].sort();
      if (JSON.stringify(sortedWatch) !== JSON.stringify(sortedSelected)) {
        useFormInstanceRef.current.setValue(name, selectedValue, { shouldValidate: true });
      } else if (typeof watchedValueRef.current === 'undefined' && !sortedSelected.length) {
        // initial set multiple to [], we should not validate yet
        useFormInstanceRef.current.setValue(name, selectedValue, { shouldValidate: false });
      }
    } else if (selectedValue && watchedValueRef.current !== selectedValue) {
      useFormInstanceRef.current.setValue(name, selectedValue, { shouldValidate: true });
    }
  }, [selectedValue, multiple, name, props.deferFormChangeOnSelectedValue]);

  //watched has been change, update label state
  useEffect(() => {
    // we only want to set label if the value is defined, since undefined can be only on init, and we have already set the label if that is the case.
    if (watchedValue !== undefined) {
      const currentSelectedLabel = getSelectedLabel(items, watchedValue, chooseLabel, !!multiple);
      setSelectedLabel(currentSelectedLabel);
    }
  }, [watchedValue, items, multiple, chooseLabel]);

  // callback when we expand/close the dropdown
  useEffect(() => {
    if (onExpand && expanded) {
      onExpand();
    } else if (onClose && !expanded) {
      onClose();
    }
  }, [onExpand, onClose, expanded]);

  // If expanded is passed in props, set that as the expanded state
  useEffect(() => {
    if (props.expanded) {
      setExpanded(props.expanded);
    }
  }, [props.expanded]);

  const disabled = props.disabled;
  const readonly = props.readonly || executing;

  let multipleObject: MultiSelectProps;

  if (multiple) {
    multipleObject = { multiple: true, visibleMultipleValues };
  } else {
    multipleObject = { multiple: false, visibleMultipleValues: undefined as never };
  }

  let errorMessageObject: InputErrorMessageSeverityType;
  if (errorMessage) {
    errorMessageObject = { errorMessage, errorSeverity };
  } else {
    errorMessageObject = { errorMessage: undefined };
  }

  const baseAttrs: SelectViewBaseProps = {
    ...viewProps,
    disabled: !!disabled,
    readonly,
    chooseLabel,
    dropdownContainerHeightType,
    expanded,
    hasOptionLabels,
    intentType,
    label,
    optGroupTuples,
    selectedLabel,
    selectedValue: watchedValue,
    setExpanded,
    updateValue
  };

  const attrs: SelectViewProps = {
    ...baseAttrs,
    ...multipleObject,
    ...errorMessageObject
  };

  return <SelectView {...attrs} ref={selectWrapperRef} />;
};
